import { supabase } from '@/core/database/supabase';
import { permissionEngine } from '@/core/permissions/permissionEngine';
import type {
  ProcessStepInput,
  ProcessStepResourceInput,
  ProcessTemplateFilters,
  ProcessTemplateInput,
  WorkProcessStep,
  WorkProcessStepResource,
  WorkProcessTemplate,
} from '../types/processTemplate.types';

function toNumber(value: unknown) {
  return Number(value || 0);
}

function normalizeResource(row: any): WorkProcessStepResource {
  return {
    id: row.id,
    company_id: row.company_id,
    template_id: row.template_id,
    step_id: row.step_id,
    resource_type: row.resource_type,
    resource_id: row.resource_id,
    resource_code: row.resource_code,
    resource_name: row.resource_name,
    unit: row.unit,
    norm_quantity: toNumber(row.norm_quantity),
    waste_factor: toNumber(row.waste_factor),
    price_source: row.price_source,
    unit_price: toNumber(row.unit_price),
    price_date: row.price_date,
    sort_order: toNumber(row.sort_order),
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

function normalizeStep(row: any): WorkProcessStep {
  return {
    id: row.id,
    company_id: row.company_id,
    template_id: row.template_id,
    step_code: row.step_code,
    step_name: row.step_name,
    category_code: row.category_code,
    unit: row.unit,
    quantity_formula: row.quantity_formula,
    default_quantity: toNumber(row.default_quantity),
    estimate_item_mode: row.estimate_item_mode || 'child',
    default_norm_id: row.default_norm_id,
    sort_order: toNumber(row.sort_order),
    status_code: row.status_code || 'active',
    created_at: row.created_at,
    updated_at: row.updated_at,
    resources: (row.resources || row.work_process_step_resources || []).map(normalizeResource),
  };
}

function normalizeTemplate(row: any): WorkProcessTemplate {
  return {
    id: row.id,
    company_id: row.company_id,
    category_code: row.category_code,
    template_code: row.template_code,
    template_name: row.template_name,
    description: row.description,
    base_unit: row.base_unit,
    default_norm_id: row.default_norm_id,
    status_code: row.status_code || 'active',
    created_by: row.created_by,
    created_at: row.created_at,
    updated_at: row.updated_at,
    steps: (row.steps || row.work_process_steps || []).map(normalizeStep),
  };
}

function buildCode(prefix: string) {
  return `${prefix}-${Math.floor(10000 + Math.random() * 90000)}`;
}

export const processTemplatesService = {
  async list(companyId: string, filters: ProcessTemplateFilters = {}): Promise<WorkProcessTemplate[]> {
    let query = supabase
      .from('work_process_templates' as any)
      .select('*')
      .eq('company_id', companyId);

    if (filters.category_code && filters.category_code !== 'all') query = query.eq('category_code', filters.category_code);
    if (filters.status_code && filters.status_code !== 'all') query = query.eq('status_code', filters.status_code);
    if (filters.search?.trim()) {
      const keyword = filters.search.trim();
      query = query.or(`template_code.ilike.%${keyword}%,template_name.ilike.%${keyword}%`);
    }

    const { data, error } = await query.order('template_code', { ascending: true });
    if (error) {
      const missingTable = error.code === '42P01' || error.message?.includes('work_process_templates');
      if (missingTable) return [];
      throw error;
    }

    return ((data || []) as any[]).map(normalizeTemplate);
  },

  async getById(id: string): Promise<WorkProcessTemplate | null> {
    const { data, error } = await supabase
      .from('work_process_templates' as any)
      .select(`
        *,
        steps:work_process_steps(
          *,
          resources:work_process_step_resources(*)
        )
      `)
      .eq('id', id)
      .order('sort_order', { referencedTable: 'work_process_steps', ascending: true })
      .maybeSingle();

    if (error) {
      const missingTable = error.code === '42P01' || error.message?.includes('work_process_templates');
      if (missingTable) return null;
      throw error;
    }

    return data ? normalizeTemplate(data) : null;
  },

  async create(userId: string, input: ProcessTemplateInput): Promise<string> {
    await permissionEngine.assert(userId, 'estimates.process_templates.create', { companyId: input.company_id });
    const { data, error } = await supabase
      .from('work_process_templates' as any)
      .insert({
        ...input,
        template_code: input.template_code?.trim() || buildCode('QT'),
        status_code: input.status_code || 'active',
      })
      .select('id')
      .single();

    if (error) throw error;
    return (data as any).id;
  },

  async update(userId: string, id: string, companyId: string, updates: Partial<ProcessTemplateInput>): Promise<void> {
    await permissionEngine.assert(userId, 'estimates.process_templates.update', { companyId });
    const { error } = await supabase
      .from('work_process_templates' as any)
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
  },

  async delete(userId: string, id: string, companyId: string): Promise<void> {
    await permissionEngine.assert(userId, 'estimates.process_templates.delete', { companyId });
    const { error } = await supabase.from('work_process_templates' as any).delete().eq('id', id);
    if (error) throw error;
  },

  async createStep(userId: string, input: ProcessStepInput): Promise<string> {
    await permissionEngine.assert(userId, 'estimates.process_templates.update', { companyId: input.company_id });
    const { data, error } = await supabase
      .from('work_process_steps' as any)
      .insert({
        ...input,
        step_code: input.step_code?.trim() || buildCode('BC'),
        default_quantity: input.default_quantity ?? 1,
        estimate_item_mode: input.estimate_item_mode || 'child',
        status_code: input.status_code || 'active',
      })
      .select('id')
      .single();

    if (error) throw error;
    return (data as any).id;
  },

  async updateStep(userId: string, id: string, companyId: string, updates: Partial<ProcessStepInput>): Promise<void> {
    await permissionEngine.assert(userId, 'estimates.process_templates.update', { companyId });
    const { error } = await supabase
      .from('work_process_steps' as any)
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
  },

  async deleteStep(userId: string, id: string, companyId: string): Promise<void> {
    await permissionEngine.assert(userId, 'estimates.process_templates.update', { companyId });
    const { error } = await supabase.from('work_process_steps' as any).delete().eq('id', id);
    if (error) throw error;
  },

  async upsertStepResources(userId: string, companyId: string, stepId: string, resources: ProcessStepResourceInput[]): Promise<void> {
    await permissionEngine.assert(userId, 'estimates.process_templates.update', { companyId });
    const { error: deleteError } = await supabase.from('work_process_step_resources' as any).delete().eq('step_id', stepId);
    if (deleteError) throw deleteError;
    if (resources.length === 0) return;

    const { error } = await supabase.from('work_process_step_resources' as any).insert(
      resources.map((resource, index) => ({
        ...resource,
        norm_quantity: resource.norm_quantity ?? 0,
        waste_factor: resource.waste_factor ?? 0,
        unit_price: resource.unit_price ?? 0,
        sort_order: resource.sort_order ?? index + 1,
      })),
    );

    if (error) throw error;
  },
};
